import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Building2,
  Trash2,
  MapPin,
  Bed,
  Bath,
  Users,
  X,
  Pencil,
  Eye,
  EyeOff,
  Calendar,
  Sparkles,
  Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const PROPERTY_TYPES = [
  "Villa",
  "Penthouse",
  "Estate",
  "Chalet",
  "Beachfront",
  "Townhouse",
  "Boutique Hotel",
  "Private Island",
  "Glass House",
];

const DEFAULT_FORM = {
  name: "",
  location: "",
  description: "",
  price_per_night: 850,
  bedrooms: 3,
  bathrooms: 3,
  max_guests: 6,
  property_type: "Villa",
  min_nights: 3,
  cleaning_fee: 250,
  address: "",
  amenities: ["Private Pool", "Concierge", "Wi-Fi"],
  highlights: ["Ocean view", "Private chef on request"],
  images: [
    "https://images.unsplash.com/photo-1776763019060-fa0663574ae6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBob3RlbCUyMHJlc29ydCUyMGludGVyaW9yfGVufDB8fHx8MTc3NzE2Njk3OXww&ixlib=rb-4.1.0&q=85",
  ],
  available: true,
};

export default function HostPortal() {
  const { user, loading } = useAuth();
  const [list, setList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = creating; string = editing
  const [form, setForm] = useState(DEFAULT_FORM);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const r = await api.get("/host/accommodations");
      setList(r.data);
    } catch (e) {
      // not host yet — silent
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login?next=/host");
      return;
    }
    if (user.role !== "host") return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  const stats = useMemo(() => {
    const total = list.length;
    const live = list.filter((a) => a.available).length;
    const sleeps = list.reduce((s, a) => s + (a.max_guests || 0), 0);
    const avgRate = total
      ? Math.round(list.reduce((s, a) => s + (a.price_per_night || 0), 0) / total)
      : 0;
    return { total, live, sleeps, avgRate };
  }, [list]);

  const becomeHost = () => {
    toast.message("Sign up as a host to manage accommodations.");
    navigate("/register?role=host");
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(DEFAULT_FORM);
    setShowForm(true);
  };

  const openEdit = (a) => {
    setEditingId(a.id);
    setForm({
      name: a.name || "",
      location: a.location || "",
      description: a.description || "",
      price_per_night: a.price_per_night || 0,
      bedrooms: a.bedrooms || 1,
      bathrooms: a.bathrooms || 1,
      max_guests: a.max_guests || 2,
      property_type: a.property_type || "Villa",
      min_nights: a.min_nights || 2,
      cleaning_fee: a.cleaning_fee || 0,
      address: a.address || "",
      amenities: a.amenities || [],
      highlights: a.highlights || [],
      images: a.images?.length ? a.images : [""],
      available: a.available !== false,
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      images: (form.images || []).filter((u) => u && u.trim()),
    };
    try {
      if (editingId) {
        await api.put(`/host/accommodations/${editingId}`, payload);
        toast.success("Updated.");
      } else {
        await api.post("/host/accommodations", payload);
        toast.success("Listed.");
      }
      setShowForm(false);
      setEditingId(null);
      setForm(DEFAULT_FORM);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this accommodation?")) return;
    try {
      await api.delete(`/host/accommodations/${id}`);
      setList((arr) => arr.filter((a) => a.id !== id));
      toast.success("Removed.");
    } catch (e) {
      toast.error("Could not delete.");
    }
  };

  const toggleAvail = async (a) => {
    try {
      const r = await api.patch(`/host/accommodations/${a.id}/availability`);
      setList((arr) => arr.map((x) => (x.id === a.id ? r.data : x)));
      toast.success(r.data.available ? "Listing is now live." : "Listing hidden.");
    } catch (e) {
      toast.error("Could not toggle.");
    }
  };

  const updateImage = (idx, value) => {
    const next = [...form.images];
    next[idx] = value;
    setForm({ ...form, images: next });
  };
  const addImage = () => setForm({ ...form, images: [...form.images, ""] });
  const removeImage = (idx) =>
    setForm({ ...form, images: form.images.filter((_, i) => i !== idx) });

  if (!user) return null;

  if (user.role !== "host") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="host-not-host">
        <div className="overline">Janté Host Network</div>
        <h1 className="font-display text-5xl text-[var(--emerald)] mt-4">Become a Host</h1>
        <p className="text-[var(--ink-2)] mt-4">
          List your villa, estate, or boutique stay to Sasha's curated short-term rental network of luxury travelers.
        </p>
        <button onClick={becomeHost} className="btn-primary mt-8" data-testid="become-host-btn">
          Apply as a Host
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16" data-testid="host-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-6">
        <div>
          <div className="overline">Host portal</div>
          <h1 className="font-display text-5xl text-[var(--emerald)] mt-3">Your luxury rentals</h1>
          <p className="text-[var(--ink-2)] mt-3 max-w-xl text-sm">
            Curate the short-term rental properties Sasha will offer to discerning Janté travelers — villas, estates, penthouses, beachfront retreats and beyond.
          </p>
        </div>
        <button onClick={openCreate} className="btn-primary" data-testid="add-accommodation-btn">
          <Plus size={14} strokeWidth={1.5} /> Add property
        </button>
      </div>

      {/* Summary stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10" data-testid="host-stats">
        <StatCard label="Properties" value={stats.total} icon={Building2} />
        <StatCard label="Live listings" value={stats.live} icon={Eye} />
        <StatCard label="Total sleeps" value={stats.sleeps} icon={Users} />
        <StatCard label="Avg rate / night" value={stats.avgRate ? `$${stats.avgRate.toLocaleString()}` : "—"} icon={Sparkles} />
      </div>

      {/* Form */}
      {showForm && (
        <div className="lux-card mt-10 relative" data-testid="accommodation-form">
          <button
            onClick={() => {
              setShowForm(false);
              setEditingId(null);
            }}
            className="absolute top-4 right-4 text-[var(--muted)] hover:text-[var(--emerald)]"
            data-testid="acc-form-close"
          >
            <X size={18} />
          </button>
          <div className="overline">{editingId ? "Edit property" : "New property"}</div>
          <h2 className="font-display text-3xl text-[var(--emerald)] mt-1">
            {editingId ? "Refine your listing" : "Tell us about your stay"}
          </h2>

          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-6 mt-6">
            <div className="sm:col-span-2 grid sm:grid-cols-3 gap-4">
              <div>
                <div className="overline mb-1">Property type</div>
                <select
                  className="lux-input"
                  value={form.property_type}
                  onChange={(e) => setForm({ ...form, property_type: e.target.value })}
                  data-testid="acc-type"
                >
                  {PROPERTY_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <div className="overline mb-1">Property name</div>
                <input
                  className="lux-input"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Casa Stellare"
                  data-testid="acc-name"
                />
              </div>
            </div>

            <div>
              <div className="overline mb-1">Public location</div>
              <input
                className="lux-input"
                required
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="e.g. Positano, Italy"
                data-testid="acc-location"
              />
            </div>
            <div>
              <div className="overline mb-1">Exact address (private)</div>
              <input
                className="lux-input"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Shared with guests after booking"
                data-testid="acc-address"
              />
            </div>

            <div className="sm:col-span-2">
              <div className="overline mb-1">Description</div>
              <textarea
                className="lux-input min-h-[110px]"
                required
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Describe the experience — views, vibe, what makes this property worthy of a Janté traveler."
                data-testid="acc-description"
              />
            </div>

            <div className="sm:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <NumField label="Price / night ($)" value={form.price_per_night} onChange={(v) => setForm({ ...form, price_per_night: v })} testId="acc-price" />
              <NumField label="Cleaning fee ($)" value={form.cleaning_fee} onChange={(v) => setForm({ ...form, cleaning_fee: v })} testId="acc-cleaning" />
              <NumField label="Min nights" value={form.min_nights} onChange={(v) => setForm({ ...form, min_nights: v })} testId="acc-min-nights" int />
              <NumField label="Max guests" value={form.max_guests} onChange={(v) => setForm({ ...form, max_guests: v })} testId="acc-guests" int />
              <NumField label="Bedrooms" value={form.bedrooms} onChange={(v) => setForm({ ...form, bedrooms: v })} testId="acc-bedrooms" int />
              <NumField label="Bathrooms" value={form.bathrooms} onChange={(v) => setForm({ ...form, bathrooms: v })} testId="acc-bathrooms" />
            </div>

            <div className="sm:col-span-2">
              <div className="overline mb-1">Amenities (comma-separated)</div>
              <input
                className="lux-input"
                value={(form.amenities || []).join(", ")}
                onChange={(e) =>
                  setForm({
                    ...form,
                    amenities: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                  })
                }
                placeholder="Private Pool, Chef, Ocean View, Wi-Fi…"
                data-testid="acc-amenities"
              />
            </div>

            <div className="sm:col-span-2">
              <div className="overline mb-1">Marketing highlights (comma-separated)</div>
              <input
                className="lux-input"
                value={(form.highlights || []).join(", ")}
                onChange={(e) =>
                  setForm({
                    ...form,
                    highlights: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                  })
                }
                placeholder="Sunset terrace, Private chef, Cliffside infinity pool…"
                data-testid="acc-highlights"
              />
            </div>

            <div className="sm:col-span-2">
              <div className="flex items-center justify-between mb-1">
                <div className="overline">Image URLs (first is the cover)</div>
                <button type="button" onClick={addImage} className="btn-ghost text-[10px]" data-testid="acc-add-image">
                  <Plus size={12} strokeWidth={1.5} /> Add image
                </button>
              </div>
              <div className="space-y-2">
                {form.images.map((u, idx) => (
                  <div key={`acc-image-${idx}-${u || "blank"}`} className="flex items-center gap-3">
                    <ImageIcon size={14} strokeWidth={1.25} className="text-[var(--gold)] shrink-0" />
                    <input
                      className="lux-input flex-1"
                      value={u}
                      onChange={(e) => updateImage(idx, e.target.value)}
                      placeholder="https://…"
                      data-testid={`acc-image-${idx}`}
                    />
                    {form.images.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        className="btn-ghost text-[var(--burgundy)]"
                        aria-label="Remove image"
                      >
                        <Trash2 size={12} strokeWidth={1.25} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="sm:col-span-2 flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-[var(--ink-2)] cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.available}
                  onChange={(e) => setForm({ ...form, available: e.target.checked })}
                  data-testid="acc-available"
                />
                List as live (visible to Janté travelers)
              </label>
            </div>

            <div className="sm:col-span-2 flex items-center gap-3">
              <button type="submit" disabled={busy} className="btn-primary" data-testid="acc-submit">
                {busy ? "Saving…" : editingId ? "Save changes" : "List property"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                }}
                className="btn-ghost"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Listings grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
        {list.length === 0 && !showForm && (
          <div className="lux-card md:col-span-2 lg:col-span-3 text-center" data-testid="host-empty">
            <Building2 size={28} strokeWidth={1.25} className="text-[var(--gold)] mx-auto" />
            <h3 className="font-display text-2xl text-[var(--emerald)] mt-3">No properties yet</h3>
            <p className="text-[var(--ink-2)] mt-2 text-sm">
              Add your first short-term rental to begin hosting Janté travelers.
            </p>
            <button onClick={openCreate} className="btn-primary mt-5" data-testid="empty-add-btn">
              <Plus size={14} strokeWidth={1.5} /> Add property
            </button>
          </div>
        )}

        {list.map((a, i) => (
          <article
            key={a.id}
            className={`lux-card flex flex-col relative ${a.available ? "" : "opacity-70"}`}
            data-testid={`accommodation-card-${i}`}
          >
            <div className="aspect-[16/10] -m-8 mb-4 overflow-hidden bg-[var(--ivory-2)] relative">
              {a.images?.[0] ? (
                <img src={a.images[0]} alt={a.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[var(--muted)]">
                  <ImageIcon size={28} strokeWidth={1.25} />
                </div>
              )}
              <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 bg-[var(--ivory)]/95 text-[10px] uppercase tracking-[0.18em] text-[var(--emerald)]">
                {a.property_type || "Villa"}
              </div>
              <div
                className={`absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] ${
                  a.available
                    ? "bg-[var(--emerald)] text-[var(--ivory)]"
                    : "bg-[var(--burgundy)] text-[var(--ivory)]"
                }`}
                data-testid={`acc-status-${i}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${a.available ? "bg-[var(--gold)]" : "bg-white"}`} />
                {a.available ? "Live" : "Hidden"}
              </div>
            </div>

            <div className="overline flex items-center gap-1.5">
              <MapPin size={12} strokeWidth={1.25} /> {a.location}
            </div>
            <h3 className="font-display text-2xl text-[var(--emerald)] mt-1">{a.name}</h3>
            <p className="text-sm text-[var(--ink-2)] mt-2 line-clamp-2">{a.description}</p>

            {a.highlights && a.highlights.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {a.highlights.slice(0, 3).map((h) => (
                  <span
                    key={`${a.id}-${h}`}
                    className="text-[10px] uppercase tracking-[0.16em] text-[var(--brand-red)] border border-[var(--gold)]/40 px-2 py-0.5"
                  >
                    {h}
                  </span>
                ))}
              </div>
            )}

            <div className="text-xs text-[var(--ink-2)] mt-4 grid grid-cols-2 gap-y-1.5">
              <span className="flex items-center gap-1.5"><Bed size={13} strokeWidth={1.25} className="text-[var(--gold)]" /> {a.bedrooms} bed</span>
              <span className="flex items-center gap-1.5"><Bath size={13} strokeWidth={1.25} className="text-[var(--gold)]" /> {a.bathrooms || 1} bath</span>
              <span className="flex items-center gap-1.5"><Users size={13} strokeWidth={1.25} className="text-[var(--gold)]" /> Sleeps {a.max_guests}</span>
              <span className="flex items-center gap-1.5"><Calendar size={13} strokeWidth={1.25} className="text-[var(--gold)]" /> Min {a.min_nights || 2} nts</span>
            </div>

            <div className="mt-4 flex items-baseline justify-between">
              <span className="font-display text-3xl text-[var(--emerald)] tabular-nums">
                ${(a.price_per_night || 0).toLocaleString()}
              </span>
              <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--ink-2)]">per night</span>
            </div>
            {a.cleaning_fee ? (
              <div className="text-[11px] text-[var(--muted)] mt-0.5">+ ${a.cleaning_fee.toLocaleString()} cleaning</div>
            ) : null}

            <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-[var(--emerald)]/10 pt-4">
              <button
                onClick={() => openEdit(a)}
                className="btn-ghost text-[var(--emerald)]"
                data-testid={`edit-acc-${i}`}
              >
                <Pencil size={13} strokeWidth={1.25} /> Edit
              </button>
              <button
                onClick={() => toggleAvail(a)}
                className="btn-ghost"
                data-testid={`toggle-acc-${i}`}
              >
                {a.available ? (
                  <><EyeOff size={13} strokeWidth={1.25} /> Hide</>
                ) : (
                  <><Eye size={13} strokeWidth={1.25} /> Show</>
                )}
              </button>
              <button
                onClick={() => remove(a.id)}
                className="btn-ghost text-[var(--burgundy)] ml-auto"
                data-testid={`remove-acc-${i}`}
              >
                <Trash2 size={13} strokeWidth={1.25} /> Remove
              </button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="lux-card flex items-center gap-4">
      <div className="w-11 h-11 border border-[var(--gold)]/40 flex items-center justify-center text-[var(--gold)] shrink-0">
        <Icon size={18} strokeWidth={1.25} />
      </div>
      <div className="min-w-0">
        <div className="overline">{label}</div>
        <div className="font-display text-2xl text-[var(--emerald)] mt-0.5 truncate">{value}</div>
      </div>
    </div>
  );
}

function NumField({ label, value, onChange, testId, int = false }) {
  return (
    <div>
      <div className="overline mb-1">{label}</div>
      <input
        className="lux-input"
        type="number"
        required
        min={0}
        step={int ? 1 : 0.5}
        value={value}
        onChange={(e) => onChange(int ? parseInt(e.target.value || "0", 10) : parseFloat(e.target.value || "0"))}
        data-testid={testId}
      />
    </div>
  );
}
